/**
 * SIL-1170 PROTOTYPE — THROWAWAY. Do not build real features on top of this
 * file; it exists to answer one question and then get deleted.
 *
 * Question: "How does a seller list an asset, given six new buyer-facing
 * fields and a live net-proceeds calculation?"
 *
 * Three structurally different treatments of the same six fields
 * (freight/pickup, data-destruction cert, warranty/support transfer,
 * power/cooling, burn-in/test report, return policy) plus a net-proceeds
 * calculation, mounted inside the EXISTING `ListForSaleCard` form
 * (apps/customer/src/components/asset-detail/EditableCards.tsx) — sub-shape
 * A per the /prototype UI skill: real header/sidebar/price fields, only this
 * subtree swaps. Switchable via `?listingVariant=A|B|C` on /assets/$assetId.
 *
 * Read-only / stub mutations only — see NOTES.md next to this file for the
 * answer this prototype is building toward and the cleanup instructions.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Upload, ChevronDown, ChevronRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';

// ---------------------------------------------------------------------------
// Shared mock field-state shape (Q1 answer — see NOTES.md for the rationale)
// ---------------------------------------------------------------------------

type FreightMode = 'buyer_pickup' | 'seller_ships_included' | 'seller_ships_billed';
type WarrantyTransfer = 'none' | 'manufacturer_transfers' | 'third_party_available' | 'negotiable';
type ReturnPolicy = 'no_returns' | 'inspection_window' | 'negotiable';
type DestructionStandard = 'nist_800_88_clear' | 'nist_800_88_purge' | 'dod_5220_22_m' | 'other';

interface SellerFieldsState {
  freightMode: FreightMode;
  freightNotes: string;
  destructionCertified: boolean;
  destructionStandard: DestructionStandard;
  destructionDocName: string | null;
  warrantyTransfer: WarrantyTransfer;
  warrantyNotes: string;
  powerVoltage: string;
  powerMaxDrawWatts: string;
  burnInDocName: string | null;
  burnInSummary: string;
  returnPolicy: ReturnPolicy;
  returnTerms: string;
}

function useSellerFieldsState() {
  return useState<SellerFieldsState>({
    freightMode: 'buyer_pickup',
    freightNotes: '',
    destructionCertified: false,
    destructionStandard: 'nist_800_88_purge',
    destructionDocName: null,
    warrantyTransfer: 'none',
    warrantyNotes: '',
    powerVoltage: '',
    powerMaxDrawWatts: '',
    burnInDocName: null,
    burnInSummary: '',
    returnPolicy: 'no_returns',
    returnTerms: '',
  });
}

// ---------------------------------------------------------------------------
// Net proceeds math (Q3 answer) — ILLUSTRATIVE RATES ONLY, not product
// numbers. The escrow fee depends on the rail the BUYER picks at checkout,
// which is unknown at listing time, so this always shows a range plus a
// clearly-labeled worst case, never a single misleadingly-precise figure.
// ---------------------------------------------------------------------------

const COMMISSION_RATE = 0.06;
/** Cheapest rail (ACH) vs. most expensive rail (card) — illustrative bounds. */
const ESCROW_FEE_RATE_BEST = 0.005;
const ESCROW_FEE_RATE_WORST = 0.03;

function netProceeds(askingPriceUsd: number) {
  const commission = askingPriceUsd * COMMISSION_RATE;
  const afterCommission = askingPriceUsd - commission;
  const escrowBest = afterCommission * ESCROW_FEE_RATE_BEST;
  const escrowWorst = afterCommission * ESCROW_FEE_RATE_WORST;
  return {
    commission,
    escrowBest,
    escrowWorst,
    netBest: afterCommission - escrowBest,
    netWorst: afterCommission - escrowWorst,
  };
}

function fmtUsd(n: number) {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// ---------------------------------------------------------------------------
// Small shared field primitives (kept local — this file is throwaway)
// ---------------------------------------------------------------------------

function Label({ children }: { children: ReactNode }) {
  return (
    <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </label>
  );
}

function RadioRow<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string; helper?: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="mt-1 space-y-1.5">
      {options.map((o) => (
        <label
          key={o.value}
          className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
            value === o.value ? 'border-brand-500 bg-brand-50' : 'border-border hover:bg-muted'
          }`}
        >
          <input
            type="radio"
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-foreground">{o.label}</span>
            {o.helper && <span className="block text-xs text-muted-foreground">{o.helper}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

function FileDropStub({
  fileName,
  onPick,
  label,
}: {
  fileName: string | null;
  onPick: (name: string) => void;
  label: string;
}) {
  // Stub only — no real upload. A real build points this at the existing
  // presign -> PUT -> finalize convention (apps/customer/src/routes/profile.tsx,
  // org-logo upload) rather than inventing a new one.
  return (
    <div className="mt-1 flex items-center gap-3 rounded-md border border-dashed border-border px-3 py-2.5 text-sm">
      <Upload className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      {fileName ? (
        <span className="truncate text-foreground">{fileName}</span>
      ) : (
        <span className="text-muted-foreground">No file attached</span>
      )}
      <button
        type="button"
        onClick={() => onPick(`${label.toLowerCase().replace(/\s+/g, '-')}.pdf`)}
        className="ml-auto shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700"
      >
        {fileName ? 'Replace' : 'Attach'}
      </button>
    </div>
  );
}

function NetProceedsLine({ askingPriceUsd }: { askingPriceUsd: number }) {
  const { commission, escrowBest, escrowWorst, netBest, netWorst } = netProceeds(askingPriceUsd);
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
      <span className="text-muted-foreground">
        {fmtUsd(askingPriceUsd)} &minus; {fmtUsd(commission)} commission (6%) &minus;{' '}
        {fmtUsd(escrowBest)}&ndash;{fmtUsd(escrowWorst)} escrow fee =
      </span>
      <span className="font-semibold text-foreground">
        {fmtUsd(netWorst)}&ndash;{fmtUsd(netBest)} net
      </span>
    </div>
  );
}

function NetProceedsCard({ askingPriceUsd }: { askingPriceUsd: number }) {
  const { commission, escrowBest, escrowWorst, netBest, netWorst } = netProceeds(askingPriceUsd);
  return (
    <Card padding="md" className="border-brand-200 bg-brand-50/40">
      <h3 className="text-sm font-semibold text-foreground">Estimated net proceeds</h3>
      <dl className="mt-3 space-y-1.5 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Asking price</dt>
          <dd className="text-foreground">{fmtUsd(askingPriceUsd)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Commission (6%)</dt>
          <dd className="text-foreground">&minus;{fmtUsd(commission)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Escrow fee (rail-dependent)</dt>
          <dd className="text-foreground">
            &minus;{fmtUsd(escrowBest)} to &minus;{fmtUsd(escrowWorst)}
          </dd>
        </div>
        <div className="mt-2 flex justify-between border-t border-border pt-2 font-semibold">
          <dt className="text-foreground">You'll receive</dt>
          <dd className="text-foreground">
            {fmtUsd(netWorst)}&ndash;{fmtUsd(netBest)}
          </dd>
        </div>
      </dl>
      <p className="mt-2 text-xs text-muted-foreground">
        Worst case assumes the buyer pays by card; best case assumes ACH. The buyer picks the
        rail at checkout, so the exact fee is unknown until then.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The six shared field blocks — same content, reused across variants so
// that what differs between A/B/C is genuinely structural, not field copy.
// ---------------------------------------------------------------------------

function SellerFieldBlocks({
  s,
  set,
}: {
  s: SellerFieldsState;
  set: <K extends keyof SellerFieldsState>(k: K, v: SellerFieldsState[K]) => void;
}) {
  return (
    <>
      <div>
        <Label>Freight &amp; pickup</Label>
        <RadioRow
          value={s.freightMode}
          onChange={(v) => set('freightMode', v)}
          options={[
            { value: 'buyer_pickup', label: 'Buyer arranges pickup' },
            { value: 'seller_ships_included', label: 'Seller ships — freight included in price' },
            { value: 'seller_ships_billed', label: 'Seller ships — freight billed to buyer' },
          ]}
        />
        <textarea
          value={s.freightNotes}
          onChange={(e) => set('freightNotes', e.target.value)}
          placeholder="Optional notes — dock hours, palletizing, lead time…"
          rows={2}
          className="mt-2 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div>
        <Label>Data destruction certification</Label>
        <select
          value={s.destructionStandard}
          onChange={(e) => set('destructionStandard', e.target.value as DestructionStandard)}
          className="mt-1 w-full max-w-sm rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
        >
          <option value="nist_800_88_clear">NIST 800-88 — Clear</option>
          <option value="nist_800_88_purge">NIST 800-88 — Purge</option>
          <option value="dod_5220_22_m">DoD 5220.22-M</option>
          <option value="other">Other (describe in the attached document)</option>
        </select>
        <FileDropStub
          label="Destruction certificate"
          fileName={s.destructionDocName}
          onPick={(n) => set('destructionDocName', n)}
        />
        <label className="mt-2 flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={s.destructionCertified}
            onChange={(e) => set('destructionCertified', e.target.checked)}
          />
          I certify data has been destroyed per the standard above
        </label>
      </div>

      <div>
        <Label>Warranty / support transfer</Label>
        <select
          value={s.warrantyTransfer}
          onChange={(e) => set('warrantyTransfer', e.target.value as WarrantyTransfer)}
          className="mt-1 w-full max-w-sm rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
        >
          <option value="none">None</option>
          <option value="manufacturer_transfers">Manufacturer warranty transfers</option>
          <option value="third_party_available">Third-party support available</option>
          <option value="negotiable">Negotiable</option>
        </select>
        <textarea
          value={s.warrantyNotes}
          onChange={(e) => set('warrantyNotes', e.target.value)}
          placeholder="Optional — expiry date, support tier, contact…"
          rows={2}
          className="mt-2 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label>Power (voltage)</Label>
          <input
            value={s.powerVoltage}
            onChange={(e) => set('powerVoltage', e.target.value)}
            placeholder="e.g. 208V 3-phase"
            className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <Label>Max power draw (W)</Label>
          <input
            value={s.powerMaxDrawWatts}
            onChange={(e) => set('powerMaxDrawWatts', e.target.value)}
            placeholder="e.g. 1600"
            className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <Label>Burn-in / test report</Label>
        <FileDropStub
          label="Burn-in report"
          fileName={s.burnInDocName}
          onPick={(n) => set('burnInDocName', n)}
        />
        <textarea
          value={s.burnInSummary}
          onChange={(e) => set('burnInSummary', e.target.value)}
          placeholder="Optional summary — hours run, tools used, pass/fail…"
          rows={2}
          className="mt-2 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div>
        <Label>Return policy</Label>
        <select
          value={s.returnPolicy}
          onChange={(e) => set('returnPolicy', e.target.value as ReturnPolicy)}
          className="mt-1 w-full max-w-sm rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
        >
          <option value="no_returns">No returns</option>
          <option value="inspection_window">Inspection window (buyer states days)</option>
          <option value="negotiable">Negotiable</option>
        </select>
        <textarea
          value={s.returnTerms}
          onChange={(e) => set('returnTerms', e.target.value)}
          placeholder="Optional terms — restocking fee, condition on return…"
          rows={2}
          className="mt-2 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Variant A — "Inline disclosure list": flat, all six fields expand in place,
// net proceeds as a single always-visible line above the submit button. No
// separate step, no side panel — the smallest change to today's form.
// ---------------------------------------------------------------------------

export function VariantA({ askingPriceUsd }: { askingPriceUsd: number }) {
  const [state, setState] = useSellerFieldsState();
  const [expanded, setExpanded] = useState(false);
  function set<K extends keyof SellerFieldsState>(k: K, v: SellerFieldsState[K]) {
    setState((prev) => ({ ...prev, [k]: v }));
  }
  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-medium text-foreground hover:bg-muted"
      >
        <span>Buyer-facing details (optional)</span>
        {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
      </button>
      {expanded && (
        <div className="space-y-5 border-t border-border px-3 py-4">
          <SellerFieldBlocks s={state} set={set} />
        </div>
      )}
      <div className="border-t border-border px-3 py-2.5">
        <NetProceedsLine askingPriceUsd={askingPriceUsd} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variant B — "Grouped cards + sticky net-proceeds receipt": the six fields
// split into three labeled groups on the left, a receipt-style card on the
// right that stays visible while the form scrolls (checkout-page pattern).
// ---------------------------------------------------------------------------

export function VariantB({ askingPriceUsd }: { askingPriceUsd: number }) {
  const [state, setState] = useSellerFieldsState();
  function set<K extends keyof SellerFieldsState>(k: K, v: SellerFieldsState[K]) {
    setState((prev) => ({ ...prev, [k]: v }));
  }
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_280px]">
      <div className="space-y-5">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Logistics
          </h4>
          <div className="mt-2 space-y-4">
            <SellerFieldBlocks
              s={state}
              set={set}
            />
          </div>
        </div>
      </div>
      <div className="lg:sticky lg:top-4 lg:self-start">
        <NetProceedsCard askingPriceUsd={askingPriceUsd} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variant C — "Progressive step after pricing": pricing/mechanic stays step
// 1 (today's form, untouched); this renders as an explicit step 2 ("Buyer-
// facing details — skippable") followed by step 3 ("Review & net proceeds")
// with big worst-case-first numbers. Demonstrates that none of the six
// fields gate activation — "Skip for now" is a first-class action, not a
// hidden default.
// ---------------------------------------------------------------------------

export function VariantC({ askingPriceUsd }: { askingPriceUsd: number }) {
  const [state, setState] = useSellerFieldsState();
  const [step, setStep] = useState<2 | 3>(2);
  function set<K extends keyof SellerFieldsState>(k: K, v: SellerFieldsState[K]) {
    setState((prev) => ({ ...prev, [k]: v }));
  }
  const { netBest, netWorst } = netProceeds(askingPriceUsd);
  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-muted px-3 py-2 text-xs font-medium text-muted-foreground">
        <span className={step === 2 ? 'text-foreground' : ''}>2. Buyer-facing details</span>
        <span>&rarr;</span>
        <span className={step === 3 ? 'text-foreground' : ''}>3. Review &amp; net proceeds</span>
      </div>
      {step === 2 ? (
        <div className="space-y-5 px-3 py-4">
          <p className="text-sm text-muted-foreground">
            None of these are required to activate the listing — skip any, or all, of them.
          </p>
          <SellerFieldBlocks s={state} set={set} />
          <div className="flex items-center gap-3 pt-2">
            <Button size="sm" onClick={() => setStep(3)}>
              Continue <ArrowRight className="ml-1 size-3.5" />
            </Button>
            <button
              type="button"
              onClick={() => setStep(3)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Skip for now
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 px-3 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              You&rsquo;ll receive (worst case &ndash; best case)
            </p>
            <p className="mt-1 text-2xl font-semibold text-foreground">
              {fmtUsd(netWorst)}&ndash;{fmtUsd(netBest)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              After 6% commission and an escrow fee that depends on the payment rail the buyer
              chooses at checkout — shown worst case first because that's the number you should
              plan around.
            </p>
          </div>
          <NetProceedsCard askingPriceUsd={askingPriceUsd} />
          <button
            type="button"
            onClick={() => setStep(2)}
            className="flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1 size-3.5" /> Back to details
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Floating variant switcher (UI.md convention) — hidden in production.
// ---------------------------------------------------------------------------

const VARIANT_ORDER = ['A', 'B', 'C'] as const;
const VARIANT_NAMES: Record<(typeof VARIANT_ORDER)[number], string> = {
  A: 'Inline disclosure list',
  B: 'Grouped cards + sticky receipt',
  C: 'Progressive step after pricing',
};

export function useListingVariant() {
  const search = useSearch({ strict: false }) as { listingVariant?: 'A' | 'B' | 'C' };
  return search.listingVariant ?? 'A';
}

export function ListingEditorPrototypeSwitcher() {
  const navigate = useNavigate();
  const variant = useListingVariant();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      if (target?.isContentEditable) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const idx = VARIANT_ORDER.indexOf(variant);
      const delta = e.key === 'ArrowLeft' ? -1 : 1;
      const next = VARIANT_ORDER[(idx + delta + VARIANT_ORDER.length) % VARIANT_ORDER.length];
      void navigate({ to: '.', search: (prev) => ({ ...prev, listingVariant: next }), replace: true });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [variant, navigate]);

  if (process.env.NODE_ENV === 'production') return null;

  function cycle(delta: 1 | -1) {
    const idx = VARIANT_ORDER.indexOf(variant);
    const next = VARIANT_ORDER[(idx + delta + VARIANT_ORDER.length) % VARIANT_ORDER.length];
    void navigate({ to: '.', search: (prev) => ({ ...prev, listingVariant: next }), replace: true });
  }

  return (
    <div className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-violet-300 bg-violet-950 px-4 py-2 text-white shadow-lg">
      <button type="button" onClick={() => cycle(-1)} aria-label="Previous variant" className="hover:text-violet-300">
        <ArrowLeft className="size-4" />
      </button>
      {/* SIL-1170: prototype variant switcher label */}
      <span className="text-xs font-medium">
        {variant} &mdash; {VARIANT_NAMES[variant]}
      </span>
      <button type="button" onClick={() => cycle(1)} aria-label="Next variant" className="hover:text-violet-300">
        <ArrowRight className="size-4" />
      </button>
    </div>
  );
}

/**
 * Mounted inside `ListForSaleCard`'s open form, after the existing
 * price/mechanic/date fields — the pricing inputs above stay real and
 * unchanged; only this subtree swaps per variant.
 */
export function ListingEditorSellerFieldsPrototype({ askingPriceUsd }: { askingPriceUsd: number }) {
  const variant = useListingVariant();
  return (
    <>
      {variant === 'A' && <VariantA askingPriceUsd={askingPriceUsd} />}
      {variant === 'B' && <VariantB askingPriceUsd={askingPriceUsd} />}
      {variant === 'C' && <VariantC askingPriceUsd={askingPriceUsd} />}
      <ListingEditorPrototypeSwitcher />
    </>
  );
}
